import { Outlet, useLocation } from 'react-router-dom';
import { BellIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import { Menubar, MenubarMenu, MenubarTrigger } from '@/components/ui-kit/menubar';
import {
  LanguageSelector,
  ProfileMenu,
  AppSidebar,
  Notification,
  useGetNotifications,
  ThemeSwitcher,
  CampaignTopBar,
} from '@/components/core';
import { cn } from '@/lib/utils';

type NotificationsData = {
  notifications: unknown[];
  unReadNotificationsCount: number;
  totalNotificationsCount: number;
};

const LegacyTopBar = ({ notifications }: { notifications: NotificationsData }) => (
  <header className="z-20 flex h-14 shrink-0 items-center justify-between border-b border-neutral-200 bg-white px-4 sm:px-6">
    <div className="flex-1" />
    <div className="flex items-center gap-1 sm:gap-2 md:gap-4">
      <ThemeSwitcher />
      <Menubar className="border-none p-0">
        <MenubarMenu>
          <MenubarTrigger
            asChild
            className="cursor-pointer p-0 focus:bg-transparent data-[state=open]:bg-transparent"
          >
            <div className="relative">
              <Button variant="ghost" size="icon" className="rounded-full hover:bg-muted">
                <BellIcon className="!h-5 !w-5 text-medium-emphasis" />
              </Button>
              {notifications.unReadNotificationsCount > 0 ? (
                <div className="absolute right-[20px] top-[13px] h-2 w-2 rounded-full bg-error" />
              ) : null}
            </div>
          </MenubarTrigger>
          <Notification />
        </MenubarMenu>
      </Menubar>
      <LanguageSelector />
      <ProfileMenu />
    </div>
  </header>
);

export const MainLayout = () => {
  const { pathname } = useLocation();
  const segments = pathname?.split('/').filter(Boolean);
  const firstSegment = segments?.[0] ?? undefined;
  const isEmailRoute = firstSegment === 'mail';
  const isChatRoute = firstSegment === 'chat';
  const isCampaignShell = !isEmailRoute && !isChatRoute;

  const { data: notificationsData } = useGetNotifications({
    Page: 0,
    PageSize: 10,
  });

  const notifications: NotificationsData = notificationsData ?? {
    notifications: [],
    unReadNotificationsCount: 0,
    totalNotificationsCount: 0,
  };

  return (
    <div className="flex h-svh w-full overflow-hidden bg-background">
      <AppSidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        {isCampaignShell ? (
          <CampaignTopBar notifications={notifications} />
        ) : (
          <LegacyTopBar notifications={notifications} />
        )}
        <div
          className={cn(
            'min-h-0 flex-1 bg-background',
            isCampaignShell ? 'flex flex-col overflow-hidden p-4 sm:p-6 2xl:p-8' : 'overflow-auto'
          )}
        >
          {isCampaignShell ? (
            <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col [&>*]:flex [&>*]:h-full [&>*]:min-h-0 [&>*]:min-w-0 [&>*]:flex-1 [&>*]:flex-col [&>*]:overflow-auto">
              <Outlet />
            </div>
          ) : (
            <Outlet />
          )}
        </div>
      </div>
    </div>
  );
};
